import { defineComponent as _defineComponent } from 'vue'
import { ClientFileCategories, UploadedFile, Client, ClientType, VersionedObject, Individual } from 'ah-api-gateways';
import FileReplacer from 'ah-common-lib/src/common/components/upload/FileReplacer.vue';
import { mergeMap } from 'rxjs/operators';
import {
  defaultAcceptanceFileExtension,
  defaultAcceptanceFileTypes,
  formatBytes,
} from 'ah-common-lib/src/helpers/file';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { getServices } from '@/app/services';
import { computed, reactive, watch } from 'vue';
import { Observable, of } from 'rxjs';
import { UploadedFileUpdate, waitForEntityChange } from 'ah-requests';


export default /*#__PURE__*/_defineComponent({
  __name: 'ClientDocumentReplacer',
  props: {
    entity: null,
    forceIndividual: { type: [Boolean, String], default: false },
    getDocumentListRequest: null,
    uploadDocumentsRequest: null,
    maxSize: { default: 10485760 },
    hideFiles: null,
    hideFooter: { type: [Boolean, String] }
  },
  emits: ["update:files"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:files', value: UploadedFile[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { entity: Client | Individual, forceIndividual: boolean | string, getDocumentListRequest?: (id: string) => Observable<UploadedFile[]>, uploadDocumentsRequest?: (
      id: string,
      category: ClientFileCategories,
      file: File
    ) => Observable<UploadedFileUpdate<VersionedObject>>, maxSize: number, hideFiles?: ClientFileCategories[], hideFooter?: boolean | string };





const state = reactive<{
  files: UploadedFile[];
  innerVisibleCollapse: string;
}>({
  files: [],
  innerVisibleCollapse: '',
});

const requestManager = useRequestManager();

const services = getServices();

const readableAccept = computed(() => defaultAcceptanceFileExtension.toUpperCase().split(',').join(', '));

const maxSizeFormatted = computed(() => formatBytes(props.maxSize));

const photoId = computed(() => state.files.find((d) => d.category === ClientFileCategories.PHOTO_ID));

const proofOfAddress = computed(() => state.files.find((d) => d.category === ClientFileCategories.PROOF_OF_ADDRESS));

const proofOfFunds = computed(() => state.files.find((d) => d.category === ClientFileCategories.PROOF_OF_FUNDS));

const IncCertif = computed(() => state.files.find((d) => d.category === ClientFileCategories.INCORPORATED_DOCUMENT));

const finStatement = computed(() => state.files.find((d) => d.category === ClientFileCategories.FINANCIAL_STATEMENT));

const invoiceSample = computed(() => state.files.find((d) => d.category === ClientFileCategories.SAMPLE_INVOICE));

const boardResolutionLetter = computed(() =>
  state.files.find((d) => d.category === ClientFileCategories.RESOLUTION_LETTER)
);

const isIndividual = computed(() => props.forceIndividual !== false || props.entity.type === ClientType.INDIVIDUAL);

const shouldHideFooter = computed(() => props.hideFooter !== false);

const getRequest = computed(() => {
  if (props.getDocumentListRequest) {
    return props.getDocumentListRequest(props.entity.id);
  } else {
    return services.client.getDocuments(props.entity.id, { errors: { silent: true } });
  }
});

function loadFiles() {
  requestManager.manager
    .sameOrCancelAndNew('getClientDocuments', getRequest.value)
    .subscribe((response) => (state.files = response));
}

function uploadFile(file: File, category: ClientFileCategories) {
  let request;
  if (props.uploadDocumentsRequest) {
    request = props.uploadDocumentsRequest(props.entity.id, category, file);
  } else {
    request = services.client.uploadDocument(props.entity.id, category, file);
  }

  return request.pipe(
    mergeMap((file) => {
      if (file.finished) {
        return waitForEntityChange(
          () => getRequest.value,
          (docs) => {
            return !!docs.find((d) => d.id === file.file.id);
          }
        ).pipe(
          mergeMap((docs) => {
            const document = docs.find((d) => d.id === file.file.id);
            if (document) {
              const currFileIndex = state.files.findIndex((i) => i.category === document.category);
              currFileIndex > -1 ? state.files.splice(currFileIndex, 1, document) : state.files.push(document);
            }
            return of(file);
          })
        );
      }
      return of(file);
    })
  );
}

function uploadID(file: File) {
  return uploadFile(file, ClientFileCategories.PHOTO_ID);
}

function uploadPOA(file: File) {
  return uploadFile(file, ClientFileCategories.PROOF_OF_ADDRESS);
}

function uploadPOF(file: File) {
  return uploadFile(file, ClientFileCategories.PROOF_OF_FUNDS);
}

function uploadINCC(file: File) {
  return uploadFile(file, ClientFileCategories.INCORPORATED_DOCUMENT);
}

function uploadFS(file: File) {
  return uploadFile(file, ClientFileCategories.FINANCIAL_STATEMENT);
}

function uploadSI(file: File) {
  return uploadFile(file, ClientFileCategories.SAMPLE_INVOICE);
}

function uploadBRL(file: File) {
  return uploadFile(file, ClientFileCategories.RESOLUTION_LETTER);
}

watch(
  () => state.files,
  () => {
    emit('update:files', state.files);
  }
);

watch(() => props.entity, loadFiles, { immediate: true });

return { __sfc: true,props, emit, state, requestManager, services, readableAccept, maxSizeFormatted, photoId, proofOfAddress, proofOfFunds, IncCertif, finStatement, invoiceSample, boardResolutionLetter, isIndividual, shouldHideFooter, getRequest, loadFiles, uploadFile, uploadID, uploadPOA, uploadPOF, uploadINCC, uploadFS, uploadSI, uploadBRL, ClientFileCategories, FileReplacer, defaultAcceptanceFileTypes }
}

})